import { type RefObject, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useStore } from 'react-redux'
import { useAuth0 } from '@auth0/auth0-react'
import {
  createApiClient,
  resolveApiBaseUrl,
  TranslationClient,
  useTranslation as useAtnTranslation,
} from '@chewy/audio-to-notes-ui'
import { v4 as uuid } from 'uuid'

import type { PuiCKEditorHandle } from '~/components/common/ckeditor/PuiCKEditor'
import { useAudioRecorderEnvironment } from '~/components/dashboard/header/hooks/useAudioRecorderEnvironment'
import SnackNotificationType from '~/constants/SnackNotificationType'
import type { RootState } from '~/store'
import { addUiNotification } from '~/store/duck/uiNotifications'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import { getDischargeNotes, getSoapId } from '~/store/reducers/soap'

const APP_NAME = 'rhapsody'

/**
 * Portal-side hook that wires the library's TranslationClient + useTranslation
 * with Auth0 and environment resolution. Calls the translation API and injects
 * the translated text into the live CKEditor instance via the Model API.
 *
 * The Model API approach is collaboration-safe: the change flows through the
 * OT pipeline, syncs to collaborators, integrates with Track Changes, and
 * fires `onChange` to keep Redux in sync automatically.
 *
 * @param editorRef - Optional ref to the CKEditor imperative handle. When
 *   provided, translated text is injected into the editor on success.
 * @returns translateNotes - resolves `true` on success, `false` on failure
 */
export const useTranslateDischargeNotes = (
  editorRef?: RefObject<PuiCKEditorHandle | null>,
) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const store = useStore<RootState>()
  const { getAccessTokenSilently } = useAuth0()
  const environment = useAudioRecorderEnvironment()

  // Memoize TranslationClient so it's stable across re-renders
  const translationClient = useMemo(() => {
    const baseUrl = resolveApiBaseUrl(undefined, environment)
    const apiClient = createApiClient(baseUrl, getAccessTokenSilently)
    return new TranslationClient(apiClient)
  }, [environment, getAccessTokenSilently])

  const { translate, isTranslating, error } = useAtnTranslation(
    translationClient,
    { appName: APP_NAME },
  )

  /**
   * Translate discharge notes to the given language code.
   *
   * @returns `true` when translation + persistence succeeded, `false` otherwise
   */
  const translateNotes = useCallback(
    async (languageCode: string): Promise<boolean> => {
      // Read latest state for IDs (these are not in the editor)
      const state = store.getState()
      const currentSoapId = getSoapId(state)
      const currentBusinessId = getCurrentBusinessId(state)

      // CKEditor is the source of truth for discharge notes content.
      // Fall back to Redux only when the editor ref is unavailable.
      const dischargeNotes =
        editorRef?.current?.getEditorData() ?? getDischargeNotes(state)

      // Guard: required IDs must be present (silent fail — no user-facing toast)
      if (!currentSoapId || !currentBusinessId) {
        return false
      }

      // Guard: discharge notes must have meaningful content.
      // CKEditor represents an empty editor as '<p>&nbsp;</p>', which is
      // truthy — use DOMParser for robust HTML-to-text extraction instead
      // of regex stripping (avoids CodeQL "incomplete sanitization" flag).
      const hasContent = (() => {
        if (!dischargeNotes) return false
        const doc = new DOMParser().parseFromString(dischargeNotes, 'text/html')
        const text = (doc.body.textContent ?? '').replace(/\u00A0/g, '').trim()
        return text.length > 0
      })()

      if (!hasContent) {
        dispatch(
          addUiNotification({
            id: uuid(),
            message: t('Errors:SHARE_LANGUAGE.EMPTY_NOTES'),
            type: SnackNotificationType.INFO,
          }),
        )
        return false
      }

      try {
        const response = await translate({
          // Safe: guarded by !hasContent check above
          text: dischargeNotes!,
          language: languageCode,
          appName: APP_NAME,
        })

        // Inject translated text into the live editor via the Model API.
        // This flows through the OT pipeline, so collaborators see it,
        // Track Changes records it, and onChange fires to update Redux.
        editorRef?.current?.replaceContent(response.text)

        return true
      } catch (err) {
        // eslint-disable-next-line no-console -- intentional: log translation errors for debugging alongside user-facing toast
        console.error('[useTranslateDischargeNotes] translation failed:', err)
        dispatch(
          addUiNotification({
            id: uuid(),
            message: t('Errors:SHARE_LANGUAGE.ERROR'),
            type: SnackNotificationType.ERROR,
          }),
        )
        return false
      }
    },
    [store, translate, dispatch, t, editorRef],
  )

  return { translateNotes, isTranslating, error }
}
